import { startSpan, addBreadcrumb, type SeverityLevel } from "@sentry/react";

// Simple types without global declarations
type HttpMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "DELETE"
  | "PATCH"
  | "HEAD"
  | "OPTIONS";

interface SentryHttpOptions extends Omit<RequestInit, "method"> {
  method: HttpMethod;
  maxBodySize?: number;
  ignoredStatusCodes?: number[];
  shouldReportError?: (response: Response) => boolean;
}

/**
 * Fetch wrapper with Sentry instrumentation for error tracking and performance monitoring
 */
export function sentryFetch(
  url: string,
  options: SentryHttpOptions,
): Promise<Response> {
  const {
    method,
    body,
    maxBodySize = 50000,
    ignoredStatusCodes = [],
    shouldReportError,
    ...fetchOptions
  } = options;

  const processedBody = body
    ? sanitizeAndTruncate(body, maxBodySize)
    : undefined;

  return startSpan(
    {
      op: "http.client",
      name: `${method} ${url.split("?")[0]}`,
    },
    async (span) => {
      try {
        span.setAttribute("http.method", method);
        span.setAttribute("http.url", url);

        if (processedBody) {
          span.setAttribute("http.request.body", processedBody);
        }

        const response = await fetch(url, { method, body, ...fetchOptions });
        span.setAttribute("http.response.status_code", response.status);

        if (!response.ok) {
          // Determine if this error should be reported to Sentry
          const shouldIgnore = ignoredStatusCodes.includes(response.status);
          const customShouldReport = shouldReportError
            ? shouldReportError(response)
            : true;

          if (!shouldIgnore && customShouldReport) {
            addHttpBreadcrumb({
              level: "error",
              message: `HTTP ${response.status} ${response.statusText}`,
              context: {
                url,
                method,
                body: processedBody,
                status: response.status,
              },
            });
          }
        }

        return response;
      } catch (error) {
        addHttpBreadcrumb({
          level: "error",
          message: error instanceof Error ? error.message : String(error),
          context: {
            url,
            method,
            body: processedBody,
          },
        });

        throw error;
      }
    },
  );
}

function addHttpBreadcrumb({
  level,
  message,
  context,
}: {
  level: SeverityLevel;
  message: string;
  context: Record<string, unknown>;
}): void {
  addBreadcrumb({
    category: "http",
    level,
    message,
    data: context,
  });
}

function sanitizeAndTruncate(
  data: unknown,
  maxSize: number,
): string | undefined {
  const sanitized = sanitiseObject(parseBody(data));
  return truncateData(sanitized, maxSize);
}

function parseBody(body: unknown): unknown {
  if (typeof body !== "string") return body;

  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

/**
 * Sanitize object by redacting sensitive information
 */
function sanitiseObject(obj: unknown): unknown {
  if (!obj || typeof obj !== "object") return obj;

  const sensitiveKeys = ["password", "token", "secret", "key", "auth"];
  const result: Record<string, unknown> = {
    ...(obj as Record<string, unknown>),
  };

  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
        result[key] = "[REDACTED]";
      } else if (typeof result[key] === "object" && result[key] !== null) {
        result[key] = sanitiseObject(result[key]);
      }
    }
  }

  return result;
}

/**
 * Truncate data to keep Sentry payloads within reasonable limits
 */
function truncateData(data: unknown, maxSize: number): string | undefined {
  if (!data) return undefined;

  try {
    const stringData = typeof data === "string" ? data : JSON.stringify(data);
    return stringData.length > maxSize
      ? `${stringData.substring(0, maxSize)}...`
      : stringData;
  } catch {
    return "[Could not stringify data]";
  }
}
